import "../styles/components/dynamic-table.css";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ApiDataContext} from "./ApiDataContext";
import * as Constants from "../utils/Constants";
import * as UrlContants from "../utils/UrlConstants";
import {seedMobiTrack, isMobiTrackLive, subscribeMobiTrack} from "../utils/mobiTrackRealtime";
import SockJS from "sockjs-client";
import axios from "axios";
import Stomp from "stompjs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinner} from "@fortawesome/free-solid-svg-icons";

import {
  getAuthToken,
  getClientIdOfUser,
  getCurrentTimeMillis,
  getDataFromBackend,
  getGeolocationFromLatLng,
  getLoggedInUserId,
  parseJsonIfNotEmpty,
  showToast,
} from "../utils/utility";

import { getRole, ROLES } from "../utils/rolePermissions";
import {convertUtcToLocal} from "../utils/dateUtils";
import NotificationIcon from "./NotificationIcon";
import {logoutFun} from "./HomeSideNavBar";
import AlertPopupDialog from "./AlertPopupDialog";
import LoadingBanner from "../utils/LoadingBanner";

import DeviceList from "./HomePage/DeviceList";
import DeviceDetailsPanel from "./HomePage/DeviceDetailsPanel";
import DashboardInsights from "./HomePage/DashboardInsights";
import DashboardHeader from "./HomePage/DashboardHeader";
import DeviceTypeActions from "./HomePage/DeviceTypeActions";
import {loadTabOrder} from "../utils/dashboardTabOrder";
import LabelMultiSelect from "./common/LabelMultiSelect";
// Shared online/staleness rules — single source of truth (see utils/deviceStatus.js).
import {isGt06Stale, isGt06PositionStale, hasReliableGpsFix} from "../utils/deviceStatus";
// Cross-remount "never let the last-ping column regress" guard (see utils/lastContactCache.js).
import {reconcileLastContact} from "../utils/lastContactCache";

export default function HomeCentralSection() {
  let { data } = useContext(ApiDataContext);
  let [apiResponse, setApiResponse] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");

  // Label filter for the dashboard device list (point 2)
  const [allLabels, setAllLabels] = useState([]);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const [labelAssetIds, setLabelAssetIds] = useState(null); // Set<assetId> or null (no filter)
  // Restore from sessionStorage so navigating away and back keeps the last selected tab/filter/view
  const [activeDeviceTab, setActiveDeviceTab] = useState(
    () => sessionStorage.getItem("dash_tab") || loadTabOrder()[0]
  );
  const [quickFilter, setQuickFilter] = useState(
    () => sessionStorage.getItem("dash_filter") || "DEFAULT"
  );
  const [viewMode, setViewMode] = useState(
    () => sessionStorage.getItem("dash_view") || "LIST"
  );
  // Skip the tab-change reset on initial mount so the restored quickFilter is preserved
  const isTabInitialMount = useRef(true);

  let [locationMap, setLocationMap] = useState({});
  let [isRefreshingLocation, setIsRefreshingLocation] = useState(false);

  let [deviceLat, setDeviceLat] = useState("");
  let [deviceLong, setDeviceLong] = useState("");
  let [assetName, setAssetName] = useState("");
  let [selectedDevice, setSelectedDevice] = useState("");
  let [selectedAssetId, setSelectedAssetId] = useState(null);
  let [lockStatus, setLockStatus] = useState("");
  let [assetDetails, setAssetDetails] = useState({});
  let [dateTime, setDateTime] = useState("");
  let [noOfSatellite, setNoOfSatellite] = useState("");
  let [backCoverStatus, setBackCoverStatus] = useState("");
  let [gsmSignalQuality, setGsmSignalQuality] = useState("");

  // Alert details
  let [lowBatteryAlert, setLowBatteryAlert] = useState("");
  let [backCoverOpenAlert, setBackCoverOpenAlert] = useState("");
  let [vibrationAlert, setVibrationAlert] = useState("");
  let [lockRopeTamperAlert, setLockRopeTamperAlert] = useState("");
  let [unlockingAlert, setUnlockingAlert] = useState("");
  let [wrongPasswordAlert, setWrongPasswordAlert] = useState("");
  let [unauthorizedRfidAlert, setUnauthorizedRfidAlert] = useState("");

  // GT06 panel state
  const [gt06AccOn, setGt06AccOn] = useState(null);
  const [gt06Speed, setGt06Speed] = useState(null);
  const [gt06VoltageLevel, setGt06VoltageLevel] = useState(null);
  const [gt06GsmSignal, setGt06GsmSignal] = useState(null);

  let [deviceTimers, setDeviceTimers] = useState([]);
  let [isLocationFetched, setLocationFetched] = useState(false);
  let [isVisible, setIsVisible] = useState(false);
  let [actionMessage, setActionMessage] = useState("");
  let [deviceStatus, setDeviceStatus] = useState("");
  let [alertState, setAlertState] = useState(new Map());

  let [showPopup, setShowPopup] = useState(false);
  let [popupData, setPopupData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  let [isPasscodeFeatureEnabled, setPasscodeFeatureEnabled] = useState(false);

  const [btLockData, setBtLockData] = useState({});
  const [selectedAssetTypeForPanel, setSelectedAssetTypeForPanel] = useState("");

  // Allocated asset unit IDs for CLIENT_EMPLOYEE (null = not yet fetched)
  const [allocatedUnitIds, setAllocatedUnitIds] = useState(null);

  const lockStatusCooldownRef = useRef({});

  const lockAudioRef = useRef(null);
  if (!lockAudioRef.current) {
    lockAudioRef.current = new Audio(process.env.PUBLIC_URL + "/lock-notification.mp3");
  }
  const prevLockStatusRef = useRef({});

  function playLockNotificationIfChanged(deviceId, locked) {
    const prev = prevLockStatusRef.current[deviceId];
    prevLockStatusRef.current[deviceId] = locked;
    if (prev === undefined || prev === locked) return;
    const audio = lockAudioRef.current;
    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  function seedLockStatus(deviceId, locked) {
    prevLockStatusRef.current[deviceId] = locked;
  }

  const selectedDeviceRef = useRef(selectedDevice);
  const deviceTimersRef = useRef(deviceTimers);
  const detailsPanelRef = useRef(null);
  // Read from inside the WebSocket handler (a stale closure over the `assetDetails` state
  // otherwise always sees whatever it was when the socket effect first ran) — same pattern as
  // selectedDeviceRef/deviceTimersRef above.
  const assetDetailsRef = useRef([]);

  useEffect(() => { selectedDeviceRef.current = selectedDevice; }, [selectedDevice]);
  useEffect(() => { deviceTimersRef.current = deviceTimers; }, [deviceTimers]);
  useEffect(() => { assetDetailsRef.current = assetDetails; }, [assetDetails]);

  // Below 1180px the two-column dashboard stacks into one column, so the
  // details panel renders *under* the device list — picking a device gave no
  // visual feedback unless the user scrolled down manually. Bring it into
  // view automatically; skip on the desktop side-by-side layout where the
  // panel is already visible and an auto-scroll would just be disorienting.
  useEffect(() => {
    if (!selectedDevice) return;
    if (typeof window === "undefined" || window.matchMedia("(min-width: 1181px)").matches) return;
    detailsPanelRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
  }, [selectedDevice]);

  useEffect(() => {
    // Skip on initial mount to preserve the sessionStorage-restored quickFilter
    if (isTabInitialMount.current) {
      isTabInitialMount.current = false;
      return;
    }
    setQuickFilter("DEFAULT");
    setSearchQuery("");
    setSelectedDevice("");
    setSelectedAssetId(null);
  }, [activeDeviceTab]);

  // Keep the OPEN Track Point detail panel's presence reconciled with the single authority
  // (isMobiTrackLive) on a tick. Socket frames flip it ONLINE/OFFLINE instantly via the
  // /topic/gpsData MOBI_TRACK branch; this catches the silent case — frames simply stop — so
  // the panel goes OFFLINE just like the list row instead of lingering on its click-time value.
  useEffect(() => {
    if (selectedAssetTypeForPanel !== "MOBI_TRACK" || !selectedDevice) return;
    // Drive the OPEN panel's presence off the SAME shared store the list and the live map read, and
    // update it on EVERY frame — so list, detail and map flip together. This covers the silent
    // on-duty case too: the server's crash-sweep OFFLINE frame lands in the store and fires this
    // callback at the same instant it flips the list row, instead of the panel trailing on a poll.
    // The periodic re-check stays (on the list's cadence) as the fallback for the rare pure-silence
    // case where no frame arrives at all, so the two surfaces never diverge.
    const apply = () =>
      setDeviceStatus(isMobiTrackLive(selectedDevice) ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE);
    apply();
    const unsubscribe = subscribeMobiTrack(selectedDevice, apply);
    const id = setInterval(apply, 15000);
    return () => {
      unsubscribe();
      clearInterval(id);
    };
  }, [selectedAssetTypeForPanel, selectedDevice]);

  // Persist tab/filter/view to sessionStorage on every change
  useEffect(() => { sessionStorage.setItem("dash_tab", activeDeviceTab); }, [activeDeviceTab]);
  useEffect(() => { sessionStorage.setItem("dash_filter", quickFilter); }, [quickFilter]);
  useEffect(() => { sessionStorage.setItem("dash_view", viewMode); }, [viewMode]);

  // For CLIENT_EMPLOYEE: fetch allocated asset unit IDs to scope the dashboard
  useEffect(() => {
    if (getRole() !== ROLES.CLIENT_EMPLOYEE) return;
    const fetchAllocated = async () => {
      try {
        const endpoint = `${Constants.API_V1}/${getClientIdOfUser()}/asset-management/user/${getLoggedInUserId()}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const resp = await axios.get(Constants.BASE_URL + endpoint);
        const raw = Array.isArray(resp.data) ? resp.data : [];
        const ids = new Set(
          raw.filter(a => !a.returnDate).map(a => a.asset?.assetUnitId).filter(Boolean)
        );
        setAllocatedUnitIds(ids);
      } catch {
        setAllocatedUnitIds(new Set());
      }
    };
    fetchAllocated();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchBtLockLatestData(assetUnitId) {
    try {
      const endPoint = `${Constants.API_V1}/bt-lock-unlock-reports/latest?clientId=${getClientIdOfUser()}&assetUnitId=${assetUnitId}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await axios.get(Constants.BASE_URL + endPoint);
      if (response.status === 200 && response.data.responseCode === "0000" && response.data.responseData) {
        const data = response.data.responseData;
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          eventTime: convertUtcToLocal(data.eventTime),
          eventType: data.eventType,
          deviceName: data.deviceName,
          imageUrl: data.imageUrl,
        };
      }
    } catch (error) {
      console.error(`Error fetching BT_LOCK data for ${assetUnitId}:`, error);
    }
    return null;
  }

  async function getAlerts(assetUnitId) {
    setPopupData(null);
    setShowPopup(true);
    const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${UrlContants.LOCATION_ALERT_REPORTS}/${UrlContants.GET_ALERTS}?assetId=${assetUnitId}`;
    try {
      const response = await getDataFromBackend(endPoint);
      if (response.status === 200) {
        setPopupData(response.data.responseData || []);
      } else if (response.status === 403) {
        logoutFun();
      } else {
        setPopupData([]);
      }
    } catch (error) {
      console.error("Error fetching alerts:", error);
      setPopupData([]);
    }
  }

  const togglePopup = () => { setShowPopup(false); setPopupData(null); };

  const closeDetailsPanel = () => { setSelectedDevice(""); setSelectedAssetId(null); };

  useEffect(() => {
    const timeout = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (data != null) {
      console.log("Raw Response Data:", data.responseData);
      if (null != data.responseData) {
        if (data.responseData.length > 0) {
          setPasscodeFeatureEnabled(data.responseData[0].client.passcodeFeature);
        }
        const transformedData = data.responseData.map((item) => {
          let additionalInfoFields = {};
          let additionalInfoJson = null;

          if (item.assetType === "GT06_TRACKER") {
            // Pre-populate from last stored GPS report so page-load shows last known data
            if (item.additionalInfo) {
              try {
                additionalInfoJson = JSON.parse(item.additionalInfo);
                // "Last ping" = last time we heard from the device (fix or no-fix). Prefer the
                // server's per-frame last_contact_utc; the additionalInfo snapshot only advances
                // on a GPS fix, so on reload it would show the last-fix time (stale) for a device
                // that's online but temporarily without a fix. Falls back to the old snapshot
                // against a backend that doesn't yet send lastContactUtc.
                //
                // reconcileLastContact guards against a *regression*: this REST fetch runs again
                // every time the dashboard is remounted (navigate away and back), and if that
                // particular response ever carries an older last-contact than what the live socket
                // already pushed this session, the row would visibly jump backwards in time and
                // could flip to Offline even though the device never actually went quiet. The cache
                // outlives the component remount; plain state doesn't.
                const freshestIso = reconcileLastContact(
                  item.assetUnitId,
                  item.lastContactUtc || additionalInfoJson.dateTime || null
                );
                additionalInfoFields = {
                  dateTimeF1: freshestIso ? convertUtcToLocal(freshestIso) : "N/A",
                  deviceStatusF2: Constants.LOADING_ARROW,
                };
              } catch (error) {
                console.error("Error parsing GT06_TRACKER additionalInfo:", error);
              }
            }
          } else if (item.additionalInfo) {
            additionalInfoJson = JSON.parse(item.additionalInfo);
            try {
              additionalInfoFields = {
                // Prefer the server's per-frame last-contact time (true "last ping"); fall back to
                // the additionalInfo snapshot for back-compat / device types that don't send it.
                dateTimeF1: item.lastContactUtc
                  ? convertUtcToLocal(item.lastContactUtc)
                  : convertUtcToLocal(additionalInfoJson.dateTime),
                deviceStatusF2: Constants.LOADING_ARROW,
                batteryStatusF3: additionalInfoJson?.batteryPercentage
                  ? !isNaN(additionalInfoJson.batteryPercentage)
                    ? additionalInfoJson.batteryPercentage + "%"
                    : additionalInfoJson.batteryPercentage
                  : "Unknown",
                lockStatusF4: additionalInfoJson.lockStatus ? Constants.LOCKED : Constants.OPEN,
                assetMovementStatusF5:
                  item.speed > 0
                    ? "Moving " + parseFloat(additionalInfoJson.speed).toFixed(2) + "kmph"
                    : "Idle",
              };
            } catch (error) {
              console.error("Error parsing additionalInfo:", error);
            }
          }

          const {
            dateTimeF1 = "N/A",
            deviceStatusF2 = Constants.LOADING_ARROW,
            batteryStatusF3 = "Unknown",
            lockStatusF4 = "N/A",
            assetMovementStatusF5 = "N/A",
          } = additionalInfoFields;

          return {
            assetId: item.assetId,
            name: item.name,
            assetUnitId: item.assetUnitId,
            assetType: item.assetType,
            vehicleType: item.vehicleType ?? null,
            // Wire protocol ("FT920" for JT808 trackers, else null/"GT06") — drives
            // FT920-specific UI such as hiding the (not-applicable) ignition status.
            protocol: item.protocol ?? null,
            dateTimeF1,
            // MOBI_TRACK has no stored telemetry status, so the shared LOADING_ARROW
            // default would spin forever (nothing resolves it for Track Point). Seed it
            // OFFLINE; the /topic/gpsData socket branch flips it ONLINE on each frame.
            deviceStatusF2: item.assetType === "MOBI_TRACK" ? Constants.DEVICE_OFFLINE : deviceStatusF2,
            batteryStatusF3,
            assetMovementStatusF5,
            lockStatusF4,
            alertStatus: "-",
            // GT06-specific fields: pre-populated from stored additionalInfo, updated by WebSocket
            ...(item.assetType === "GT06_TRACKER" ? {
              accOn: additionalInfoJson?.accOn ?? null,
              speedKmph: additionalInfoJson?.speed ?? null,
              voltageLevel: additionalInfoJson?.voltageLevel ?? null,
              gsmSignal: additionalInfoJson?.gsmSignal ?? null,
              // Freshness/staleness must also track last contact, not last fix — otherwise an
              // online-but-no-fix device wrongly reads stale/OFFLINE after a reload. Same
              // never-regress guard as dateTimeF1 above — isGt06Stale reads this field directly,
              // so an older-than-already-known value here would wrongly flip the row Offline.
              lastFrameUtc: reconcileLastContact(
                item.assetUnitId,
                item.lastContactUtc || additionalInfoJson?.dateTime || null
              ),
              // Fix-ONLY timestamp (never merged with lastContactUtc) — additionalInfo is only
              // rewritten server-side on a real GPS fix, so this is exactly "when did we last
              // actually see this device move", distinct from lastFrameUtc above (which a bare
              // heartbeat also advances). isGt06PositionStale reads this to decide whether the
              // displayed speed is still trustworthy.
              lastFixUtc: additionalInfoJson?.dateTime ?? null,
            } : {}),
          };
        });

        const tempAssetDetails = data.responseData.map((item) => ({
          assetUnitId: item.assetUnitId,
          details: parseJsonIfNotEmpty(item.additionalInfo),
        }));

        setApiResponse(transformedData);
        setAssetDetails(tempAssetDetails);

        let initialTimers = data.responseData.map((item) => ({
          assetUnitId: item.assetUnitId,
          lastLoadtime: getCurrentTimeMillis(),
        }));
        setDeviceTimers(initialTimers);
        setTimeout(fetchData, 2800);
        fetchOnlineStatus();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  async function fetchData() {
    try {
      const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${UrlContants.LOCATION_ALERT_REPORTS}/${UrlContants.GET_ALERTS_COUNT}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await getDataFromBackend(endPoint);
      if (response.status === 200) {
        const alertCountList = response.data.responseData;
        const map = new Map(alertState);
        alertCountList.forEach((item) => {
          map.set(item.assetUnitId, item.alertCount);
          updateObjectFieldsById(item.assetUnitId, {
            alertStatus: item.alertCount > 0
              ? <NotificationIcon onClick={() => getAlerts(item.assetUnitId)} />
              : "-",
          });
        });
        setAlertState(map);
      } else if (response.status === 403) {
        logoutFun();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  // Coordinate-keyed reverse-geocode cache — avoids re-geocoding identical/unchanged
  // coordinates across refreshes (critical at large fleet sizes).
  const geocodeCacheRef = useRef({});
  const cachedGeocode = async (lat, lng) => {
    const key = `${lat},${lng}`;
    if (geocodeCacheRef.current[key]) return geocodeCacheRef.current[key];
    const result = await getGeolocationFromLatLng(lat, lng);
    if (result) geocodeCacheRef.current[key] = result;
    return result;
  };
  // Bounded-concurrency runner so we don't fire thousands of requests at once.
  const runPool = async (items, worker, size = 8) => {
    for (let i = 0; i < items.length; i += size) {
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(items.slice(i, i + size).map(worker));
    }
  };

  const fetchAndUpdateLocation = async () => {
    if (apiResponse.length > 0 && assetDetails.length > 0) {
      setIsRefreshingLocation(true);
      const newLocations = {};
      const newBtLockData = {};

      const processItem = async (item) => {
          if (item.assetType === "BT_LOCK") {
            const btLockInfo = await fetchBtLockLatestData(item.assetUnitId);
            if (btLockInfo) {
              newBtLockData[item.assetUnitId] = btLockInfo;
              const locationStr = await cachedGeocode(btLockInfo.latitude, btLockInfo.longitude);
              newLocations[item.assetUnitId] = locationStr || "Address not found";
              updateObjectFieldsById(item.assetUnitId, {
                dateTimeF1: btLockInfo.eventTime,
                lockStatusF4: btLockInfo.eventType === "LOCK" ? Constants.LOCKED : Constants.OPEN,
              });
            } else {
              newLocations[item.assetUnitId] = "No recent activity";
              updateObjectFieldsById(item.assetUnitId, { dateTimeF1: "No data available", lockStatusF4: "Unknown" });
            }
          } else if (item.assetType === "MOBI_TRACK" || item.assetType === "GT06_TRACKER") {
            // Location resolved from assetDetails (populated by WebSocket for GT06)
            const correspondingAssetDetails = assetDetails.find((d) => d.assetUnitId === item.assetUnitId);
            if (correspondingAssetDetails?.details?.latitude && correspondingAssetDetails?.details?.longitude) {
              const locationStr = await cachedGeocode(
                correspondingAssetDetails.details.latitude,
                correspondingAssetDetails.details.longitude
              );
              newLocations[item.assetUnitId] = locationStr || "Address not found";
            } else {
              newLocations[item.assetUnitId] = item.assetType === "GT06_TRACKER" ? "Awaiting first report" : "Not Available";
            }
          } else {
            const correspondingAssetDetails = assetDetails.find((d) => d.assetUnitId === item.assetUnitId);
            if (correspondingAssetDetails?.details) {
              const { latitude, longitude } = correspondingAssetDetails.details;
              if (latitude && longitude) {
                const locationStr = await cachedGeocode(latitude, longitude);
                newLocations[item.assetUnitId] = locationStr || "Address not found";
              } else {
                newLocations[item.assetUnitId] = "GPS Invalid";
              }
            } else {
              newLocations[item.assetUnitId] = "No Details";
            }
          }
      };

      await runPool(apiResponse, processItem, 8);

      setLocationMap((prev) => ({ ...prev, ...newLocations }));
      setBtLockData((prev) => ({ ...prev, ...newBtLockData }));
      setIsRefreshingLocation(false);
    }
  };

  useEffect(() => {
    if (apiResponse.length > 0 && assetDetails.length > 0 && !isLocationFetched) {
      fetchAndUpdateLocation();
      setLocationFetched(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse, assetDetails, isLocationFetched]);

  async function fetchOnlineStatus() {
    try {
      const endPoint = `${Constants.API_V1}/${getClientIdOfUser()}/${Constants.EP_ASSETS_ONLINE_STATUS}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await axios.get(Constants.BASE_URL + endPoint);
      if (response.status === 200 && response.data.responseData) {
        const onlineIds = new Set(response.data.responseData);
        setApiResponse((prev) =>
          prev.map((item) => {
            // Only update GPS_LOCK and GT06_TRACKER from REST status endpoint
            if (item.assetType !== "GPS_LOCK" && item.assetType !== "GT06_TRACKER") return item;
            const isOnline = onlineIds.has(item.assetUnitId);
            // Rule: OFFLINE ⇒ ignition is OFF. We only trust ACC=ON when the
            // device is actively transmitting; otherwise the last-known value
            // is stale and must not be displayed as "ignition ON".
            const next = {
              ...item,
              deviceStatusF2: isOnline ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE,
            };
            if (!isOnline && item.assetType === "GT06_TRACKER") {
              next.accOn = false;
            }
            // Override: even if the TCP channel is alive, treat the device as OFFLINE
            // when the last GPS frame is older than the staleness threshold.
            if (item.assetType === "GT06_TRACKER" && isGt06Stale(item.lastFrameUtc)) {
              next.deviceStatusF2 = Constants.DEVICE_OFFLINE;
              next.accOn = false;
              next.speedKmph = null;
            }
            // A device can stay ONLINE indefinitely on heartbeats alone (lastFrameUtc, above,
            // also advances on a bare heartbeat) while its last real GPS fix ages — there's no
            // live truth source for speed without one, so null it independently of the
            // connectivity check. Ignition is untouched here: it already reflects the real,
            // live heartbeat accOn (see the WS handler), so it shouldn't be forced off just
            // because the position itself has gone stale.
            if (item.assetType === "GT06_TRACKER" && isGt06PositionStale(item.lastFixUtc)) {
              next.speedKmph = null;
            }
            return next;
          })
        );
      }
    } catch (error) {
      console.error("Error fetching online status:", error);
    }
  }

  useEffect(() => {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const stompClient = Stomp.over(socket);

    const updateDeviceTimers = (updatedAssetUnitId) => {
      const currentMillis = getCurrentTimeMillis();
      const currentTimers = deviceTimersRef.current;
      const updatedDeviceTimers = [...currentTimers];
      const index = updatedDeviceTimers.findIndex((t) => t.assetUnitId === updatedAssetUnitId);
      if (index !== -1) {
        updatedDeviceTimers[index].lastLoadtime = currentMillis;
        setDeviceTimers(updatedDeviceTimers);
      }
    };

    stompClient.connect({}, (frame) => {
      console.log("Connected: " + frame);

      stompClient.subscribe("/topic/gpsData", (message) => {
        try {
          const messageData = JSON.parse(message.body);
          if (!messageData || typeof messageData !== "object") return;

          // --- GT06_TRACKER branch ---
          if (messageData.assetType === "GT06_TRACKER") {
            // Build the update object conditionally — heartbeat and login-presence
            // frames omit lat/lon/speed and we must not overwrite the last known
            // GPS values with undefined.
            const has = (v) => v !== undefined && v !== null;
            const hasGpsFix = hasReliableGpsFix(
              has(messageData.latitude),
              has(messageData.longitude),
              messageData.gpsFix
            );

            const updates = { deviceStatusF2: Constants.DEVICE_ONLINE };
            if (messageData.dateTime) {
              // Keep the freshest-known cache in lockstep with the live frame too, so a later
              // remount's REST re-seed can never regress below what the socket just showed.
              const freshestIso = reconcileLastContact(messageData.deviceId, messageData.dateTime);
              updates.dateTimeF1 = convertUtcToLocal(freshestIso);
              updates.lastFrameUtc = freshestIso;
            }
            if (has(messageData.accOn)) updates.accOn = messageData.accOn;
            if (has(messageData.speed)) updates.speedKmph = messageData.speed;
            if (has(messageData.voltageLevel)) updates.voltageLevel = messageData.voltageLevel;
            if (has(messageData.gsmSignal)) updates.gsmSignal = messageData.gsmSignal;
            // lastFixUtc only advances on a REAL fix — a heartbeat's own dateTime (already
            // captured in updates.lastFrameUtc above) must never count as a fresh position,
            // or the speed-staleness check would never fire for a heartbeat-only device.
            if (hasGpsFix && messageData.dateTime) updates.lastFixUtc = messageData.dateTime;
            updateObjectFieldsById(messageData.deviceId, updates);

            if (hasGpsFix) {
              const existingDetails = assetDetailsRef.current.find(
                (d) => d.assetUnitId === messageData.deviceId
              );
              const hadNoPriorFix = !(
                existingDetails?.details?.latitude && existingDetails?.details?.longitude
              );
              updateLatitudeAndLongitude(messageData.deviceId, messageData.latitude, messageData.longitude);
              if (hadNoPriorFix) {
                // The list row's address text is a point-in-time snapshot — resolved once on
                // load (see fetchAndUpdateLocation), not re-geocoded on every live position
                // update, so a moving fleet doesn't re-hit the geocoder on every ping. But a
                // device going from "never had a fix" ("Awaiting first report") to "just got
                // one" has no existing address to preserve, so resolve it once, for just this
                // device, instead of leaving it stuck on the placeholder until a manual refresh.
                cachedGeocode(messageData.latitude, messageData.longitude).then((locationStr) => {
                  setLocationMap((prev) => ({
                    ...prev,
                    [messageData.deviceId]: locationStr || "Address not found",
                  }));
                });
              }
            }
            updateDeviceTimers(messageData.deviceId);

            if (selectedDeviceRef.current === messageData.deviceId) {
              if (has(messageData.accOn)) setGt06AccOn(messageData.accOn);
              if (has(messageData.speed)) setGt06Speed(messageData.speed);
              if (has(messageData.voltageLevel)) setGt06VoltageLevel(messageData.voltageLevel);
              if (has(messageData.gsmSignal)) setGt06GsmSignal(messageData.gsmSignal);
              if (hasGpsFix) {
                setDeviceLat(messageData.latitude);
                setDeviceLong(messageData.longitude);
              }
              if (messageData.dateTime) setDateTime(convertUtcToLocal(messageData.dateTime));
              setDeviceStatus(Constants.DEVICE_ONLINE);
            }
            return;
          }

          // --- MOBI_TRACK branch (Track Point live location over the shared socket) ---
          // Must run BEFORE the GPS_LOCK branch: without this, a MOBI_TRACK frame for the
          // selected device would fall through and be mis-handled as a lock report.
          if (messageData.assetType === "MOBI_TRACK") {
            // Explicit duty-off signal from the driver app — flip the row (and the detail
            // panel, if this device is selected) OFFLINE immediately rather than waiting
            // out the silence timeout.
            if (messageData.status === "OFFLINE") {
              updateObjectFieldsById(messageData.deviceId, {
                deviceStatusF2: Constants.DEVICE_OFFLINE,
              });
              if (selectedDeviceRef.current === messageData.deviceId) {
                setDeviceStatus(Constants.DEVICE_OFFLINE);
              }
              return;
            }
            const hasGps = messageData.latitude != null && messageData.longitude != null;
            const updates = { deviceStatusF2: Constants.DEVICE_ONLINE };
            if (messageData.timestamp) {
              updates.dateTimeF1 = convertUtcToLocal(messageData.timestamp);
              updates.lastFrameUtc = messageData.timestamp;
            }
            updateObjectFieldsById(messageData.deviceId, updates);
            if (hasGps) {
              updateLatitudeAndLongitude(messageData.deviceId, messageData.latitude, messageData.longitude);
            }
            updateDeviceTimers(messageData.deviceId);
            if (selectedDeviceRef.current === messageData.deviceId) {
              if (hasGps) {
                setDeviceLat(messageData.latitude);
                setDeviceLong(messageData.longitude);
              }
              if (messageData.timestamp) setDateTime(convertUtcToLocal(messageData.timestamp));
              setDeviceStatus(Constants.DEVICE_ONLINE);
            }
            return;
          }

          // --- GPS_LOCK / existing branch ---
          if (selectedDeviceRef.current === messageData.deviceId) {
            if (messageData.command === "P43") {
              playLockNotificationIfChanged(messageData.deviceId, messageData.unlockStatus !== 1);
              updatgps_lockStatus(messageData.unlockStatus !== 1);
              setActionMessage(messageData.unlockStatus !== 1 ? Constants.FAIL : Constants.SUCCESS);
              lockStatusCooldownRef.current[messageData.deviceId] = Date.now();
            } else {
              setDeviceLat(messageData.latitude);
              setDeviceLong(messageData.longitude);
              const cooldownUntil = lockStatusCooldownRef.current[messageData.deviceId] || 0;
              if (Date.now() - cooldownUntil > 10000) {
                playLockNotificationIfChanged(messageData.deviceId, messageData.lockStatus);
                updatgps_lockStatus(messageData.lockStatus);
              }
              updateBackCoverStatus(messageData.backCoverStatus);
              setDateTime(convertUtcToLocal(messageData.dateTime));
              setNoOfSatellite(messageData.numberOfSatellite);
              setGsmSignalQuality(messageData.gsmSignalQuality);
              setLowBatteryAlert(messageData.lowBatteryAlert);
              setBackCoverOpenAlert(messageData.backCoverOpenAlert);
              setUnlockingAlert(messageData.unlockingAlert);
              setWrongPasswordAlert(messageData.wrongPasswordAlert);
              setVibrationAlert(messageData.vibrationAlert);
              setLockRopeTamperAlert(messageData.lockRopeTamperAlert);
              setUnauthorizedRfidAlert(messageData.unauthorizedRfidAlert);
            }
            setDeviceStatus(Constants.DEVICE_ONLINE);
          }

          if (messageData.command !== "P43") {
            if (messageData.alert) {
              setAlertState((prevAlertState) => {
                const map = new Map(prevAlertState);
                map.set(messageData.deviceId, (map.get(messageData.deviceId) || 0) + 1);
                return map;
              });
            }

            const cooldownActive = Date.now() - (lockStatusCooldownRef.current[messageData.deviceId] || 0) <= 10000;
            const tableFields = {
              dateTimeF1: convertUtcToLocal(messageData.dateTime),
              deviceStatusF2: Constants.DEVICE_ONLINE,
              batteryStatusF3: messageData.batteryPercentage
                ? !isNaN(messageData.batteryPercentage)
                  ? messageData.batteryPercentage + "%"
                  : messageData.batteryPercentage
                : "Unknown",
              assetMovementStatusF5:
                messageData.speed > 0
                  ? "Moving " + parseFloat(messageData.speed).toFixed(2) + "kmph"
                  : "Idle",
            };
            if (!cooldownActive) {
              tableFields.lockStatusF4 = messageData.lockStatus ? Constants.LOCKED : Constants.OPEN;
            }
            updateObjectFieldsById(messageData.deviceId, tableFields);

            if (messageData.alert) {
              updateObjectFieldsById(messageData.deviceId, {
                alertStatus: <NotificationIcon onClick={() => getAlerts(messageData.deviceId)} />,
              });
            }
            updateLatitudeAndLongitude(messageData.deviceId, messageData.latitude, messageData.longitude);
          } else {
            updateObjectFieldsById(messageData.deviceId, {
              lockStatusF4: messageData.unlockStatus !== 1 ? Constants.LOCKED : Constants.OPEN,
            });
            if (messageData.unlockStatus === 1) {
              updateObjectFieldsById(messageData.deviceId, {
                alertStatus: <NotificationIcon onClick={() => getAlerts(messageData.deviceId)} />,
              });
            }
            updateDetailsFieldById(messageData.deviceId, {
              lockStatus: messageData.unlockStatus !== 1 ? Constants.LOCK_CLOSE : Constants.LOCK_OPEN,
            });
          }
          updateDeviceTimers(messageData.deviceId);
        } catch (error) {
          console.error("Invalid JSON: ", error);
        }
      });

      stompClient.subscribe(Constants.TOPIC_DEVICE_STATUS, (message) => {
        try {
          const statusData = JSON.parse(message.body);
          if (statusData?.deviceId && statusData?.status) {
            const isOnline = statusData.status === "ONLINE";
            // Rule: OFFLINE ⇒ ignition is OFF (force the row's accOn to false).
            const fields = {
              deviceStatusF2: isOnline ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE,
            };
            if (!isOnline) fields.accOn = false;
            updateObjectFieldsById(statusData.deviceId, fields);
            // Refresh the per-device timer on every ONLINE ping. Without this
            // the dashboard's OFFLINE_TIMEOUT (30s) will mark a GT06 tracker
            // OFFLINE once after login until its first GPS frame arrives,
            // even though heartbeats are still flowing.
            if (isOnline) {
              updateDeviceTimers(statusData.deviceId);
            }
            if (selectedDeviceRef.current === statusData.deviceId) {
              setDeviceStatus(isOnline ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE);
              if (!isOnline) setGt06AccOn(false);
            }
          }
        } catch (error) {
          console.error("Invalid device status message: ", error);
        }
      });
    });

    return () => {
      if (stompClient?.connected) stompClient.disconnect();
    };
  }, []);

  const showConfirmation = (message) => {
    if (window.confirm("Are you sure you want to proceed?")) {
      sendCommandToDevice(message);
    }
  };

  function sendCommandToDevice(message) {
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const independentStompClient = Stomp.over(socket);
    setIsVisible(true);
    setActionMessage(<span id="actionMessage"><FontAwesomeIcon icon={faSpinner} spin /></span>);
    setTimeout(() => setActionMessage(""), 15000);
    independentStompClient.connect({}, (frame) => {
      try {
        independentStompClient.send("/app/send-command", {}, JSON.stringify({ command: message, deviceId: selectedDevice }));
        if (independentStompClient?.connected) independentStompClient.disconnect();
      } catch (e) {
        console.log(e);
      }
    });
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      for (const device of deviceTimers) {
        if (getCurrentTimeMillis() - device.lastLoadtime > Constants.OFFLINE_TIMEOUT) {
          // Rule: OFFLINE ⇒ ignition is OFF (force accOn false on the row).
          updateObjectFieldsById(device.assetUnitId, {
            deviceStatusF2: Constants.DEVICE_OFFLINE,
            assetMovementStatusF5: Constants.NO_INFO,
            accOn: false,
          });
          // Only flip the details panel status if the timing-out device is
          // actually the one being viewed. Without this guard any other device
          // hitting the timeout would clobber the panel status, leaving the
          // table and the details panel out of sync.
          if (selectedDeviceRef.current === device.assetUnitId) {
            setDeviceStatus(Constants.DEVICE_OFFLINE);
            setGt06AccOn(false);
          }
        }
      }

      // GT06 staleness sweep: mark any tracker whose last GPS frame is older
      // than GT06_STALE_THRESHOLD_MS as OFFLINE, regardless of TCP channel state.
      setApiResponse((prev) =>
        prev.map((d) => {
          if (d.assetType !== "GT06_TRACKER") return d;
          // Position-only staleness: a device can stay ONLINE indefinitely on heartbeats
          // alone while its last real GPS fix ages, so this null's the displayed speed
          // independently of (and even when) the connectivity check below finds nothing
          // stale — there's no live truth source for speed without a fix. Ignition is
          // untouched: it already tracks the real, live heartbeat accOn.
          const positionStale = isGt06PositionStale(d.lastFixUtc);
          if (!isGt06Stale(d.lastFrameUtc)) {
            if (positionStale && d.speedKmph != null) {
              if (selectedDeviceRef.current === d.assetUnitId) setGt06Speed(null);
              return { ...d, speedKmph: null };
            }
            return d;
          }
          if (
            d.deviceStatusF2 === Constants.DEVICE_OFFLINE &&
            d.accOn === false &&
            d.speedKmph == null
          )
            return d; // already in correct state
          if (selectedDeviceRef.current === d.assetUnitId) {
            setDeviceStatus(Constants.DEVICE_OFFLINE);
            setGt06AccOn(false);
            setGt06Speed(null);
          }
          return { ...d, deviceStatusF2: Constants.DEVICE_OFFLINE, accOn: false, speedKmph: null };
        })
      );
    }, 30000);
    return () => clearInterval(intervalId);
  }, [deviceTimers]);

  const updateObjectFieldsById = (assetUnitId, updatedFields) => {
    setApiResponse((prevApiResponse) => {
      const indexToUpdate = prevApiResponse.findIndex((obj) => obj.assetUnitId === assetUnitId);
      if (indexToUpdate !== -1) {
        const newArray = [...prevApiResponse];
        newArray[indexToUpdate] = { ...newArray[indexToUpdate], ...updatedFields };
        return newArray;
      }
      return prevApiResponse;
    });
  };

  const updateLatitudeAndLongitude = (assetUnitId, latitude, longitude) => {
    setAssetDetails((prevDetails) =>
      prevDetails.map((detail) =>
        detail.assetUnitId === assetUnitId
          ? { ...detail, details: { ...detail.details, latitude, longitude } }
          : detail
      )
    );
  };

  const updateDetailsFieldById = (assetUnitId, updatedFields) => {
    setAssetDetails((prevDetails) => {
      const indexToUpdate = prevDetails.findIndex((d) => d.assetUnitId === assetUnitId);
      if (indexToUpdate !== -1) {
        const newArray = [...prevDetails];
        newArray[indexToUpdate] = {
          ...newArray[indexToUpdate],
          details: { ...newArray[indexToUpdate].details, ...updatedFields },
        };
        return newArray;
      }
      return prevDetails;
    });
  };

  const handleRowClick = async (rowData) => {
    console.log("Row clicked:", rowData);
    setSelectedDevice(rowData.assetUnitId);
    setAssetName(rowData.name);
    setSelectedAssetId(rowData.assetId);
    setSelectedAssetTypeForPanel(rowData.assetType);

    if (rowData.assetType === "BT_LOCK") {
      const cached = btLockData[rowData.assetUnitId];
      if (cached) {
        setDeviceLat(cached.latitude);
        setDeviceLong(cached.longitude);
        setDateTime(cached.eventTime);
        seedLockStatus(rowData.assetUnitId, cached.eventType === "LOCK");
        updatgps_lockStatus(cached.eventType === "LOCK");
        setDeviceStatus(rowData.deviceStatusF2);
        setNoOfSatellite(""); setBackCoverStatus(""); setGsmSignalQuality("");
        setLowBatteryAlert(""); setBackCoverOpenAlert(""); setUnlockingAlert("");
        setWrongPasswordAlert(""); setVibrationAlert(""); setLockRopeTamperAlert(""); setUnauthorizedRfidAlert("");
        setActionMessage("");
      } else {
        resetDeviceDetails();
        setDeviceStatus(rowData.deviceStatusF2);
      }
      const btLockInfo = await fetchBtLockLatestData(rowData.assetUnitId);
      if (btLockInfo) {
        setDeviceLat(btLockInfo.latitude);
        setDeviceLong(btLockInfo.longitude);
        setDateTime(btLockInfo.eventTime);
        seedLockStatus(rowData.assetUnitId, btLockInfo.eventType === "LOCK");
        updatgps_lockStatus(btLockInfo.eventType === "LOCK");
      } else if (!cached) {
        setDateTime("No activity recorded");
        setLockStatus("Unknown");
        setDeviceStatus("No Data");
      }
    } else if (rowData.assetType === "MOBI_TRACK") {
      const existingDetails = assetDetails.find((d) => d.assetUnitId === rowData.assetUnitId);
      setDeviceLat(existingDetails?.details?.latitude || "");
      setDeviceLong(existingDetails?.details?.longitude || "");
      setDateTime(rowData.dateTimeF1 || "");
      setDeviceStatus(rowData.deviceStatusF2);
      setLockStatus(""); setNoOfSatellite(""); setBackCoverStatus(""); setGsmSignalQuality("");
      setLowBatteryAlert(""); setBackCoverOpenAlert(""); setUnlockingAlert("");
      setWrongPasswordAlert(""); setVibrationAlert(""); setLockRopeTamperAlert(""); setUnauthorizedRfidAlert("");
      setActionMessage("");
      try {
        const endPoint = `${Constants.API_AUTH_V1}/mobi-track/latest/${rowData.assetUnitId}`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const response = await axios.get(Constants.BASE_URL + endPoint);
        if (response.status === 200 && response.data) {
          const d = response.data;
          setDeviceLat(d.latitude || "");
          setDeviceLong(d.longitude || "");
          setDateTime(d.timestamp ? convertUtcToLocal(d.timestamp) : "No activity");
          // Feed the SAME shared store the list uses, then read presence back from it — so
          // the detail panel and the list row can never disagree (single source of truth).
          seedMobiTrack(rowData.assetUnitId, d);
          // Use the Constants elements (not raw strings) so the badge styles online =
          // green, matching every other device type and the socket-updated row status.
          setDeviceStatus(isMobiTrackLive(rowData.assetUnitId) ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE);
        } else {
          setDeviceStatus(Constants.DEVICE_OFFLINE);
        }
      } catch (error) {
        console.error("MOBI_TRACK click fetch error", error);
        setDeviceStatus(Constants.DEVICE_OFFLINE);
      }
    } else if (rowData.assetType === "GT06_TRACKER") {
      // Populate from cached WebSocket data in apiResponse
      const existingDetails = assetDetails.find((d) => d.assetUnitId === rowData.assetUnitId);
      const rowStatus = rowData.deviceStatusF2 || "OFFLINE";
      const rowOffline = rowStatus !== Constants.DEVICE_ONLINE || isGt06Stale(rowData.lastFrameUtc);
      // Speed has no live truth source without a real fix — gated separately from rowOffline
      // (connectivity) so a heartbeat-only-but-still-online device doesn't keep showing its
      // last known speed as current motion. Ignition is untouched by this: it already
      // reflects the real, live heartbeat accOn.
      const positionStale = isGt06PositionStale(rowData.lastFixUtc);
      setDeviceLat(existingDetails?.details?.latitude || "");
      setDeviceLong(existingDetails?.details?.longitude || "");
      setDateTime(rowData.dateTimeF1 || "");
      setDeviceStatus(rowStatus);
      // Rule: OFFLINE ⇒ ignition is OFF, regardless of the last cached value.
      setGt06AccOn(rowOffline ? false : (rowData.accOn ?? null));
      setGt06Speed(rowOffline || positionStale ? null : (rowData.speedKmph ?? null));
      setGt06VoltageLevel(rowData.voltageLevel ?? null);
      setGt06GsmSignal(rowData.gsmSignal ?? null);
      // Clear GPS_LOCK-specific state
      setLockStatus(""); setNoOfSatellite(""); setBackCoverStatus(""); setGsmSignalQuality("");
      setLowBatteryAlert(""); setBackCoverOpenAlert(""); setUnlockingAlert("");
      setWrongPasswordAlert(""); setVibrationAlert(""); setLockRopeTamperAlert(""); setUnauthorizedRfidAlert("");
      setActionMessage("");
    } else {
      // GPS_LOCK
      const existingDetails = assetDetails.find((d) => d.assetUnitId === rowData.assetUnitId);
      if (existingDetails?.details) {
        findAssetAndShowDetails(rowData.assetUnitId);
      } else {
        resetDeviceDetails();
        setDeviceStatus(rowData.deviceStatusF2);
      }
    }
  };

  function resetDeviceDetails() {
    setDeviceLat("");
    setDeviceLong("");
    setLockStatus("");
    setNoOfSatellite("");
    setBackCoverStatus("");
    setDateTime("");
    setGsmSignalQuality("");
    setLowBatteryAlert("");
    setBackCoverOpenAlert("");
    setUnlockingAlert("");
    setWrongPasswordAlert("");
    setVibrationAlert("");
    setLockRopeTamperAlert("");
    setUnauthorizedRfidAlert("");
    setActionMessage("");
    setGt06AccOn(null);
    setGt06Speed(null);
    setGt06VoltageLevel(null);
    setGt06GsmSignal(null);
  }

  const getFieldValueById = (assetUnitId, field) => {
    const asset = apiResponse.find((obj) => obj.assetUnitId === assetUnitId);
    return asset ? asset[field] : null;
  };

  const findAssetAndShowDetails = (assetUnitId) => {
    const details = assetDetails.find((item) => item.assetUnitId === assetUnitId);
    if (details?.details) {
      const detail = details.details;
      const isLocked = getFieldValueById(assetUnitId, "lockStatusF4") !== Constants.OPEN;
      seedLockStatus(assetUnitId, isLocked);
      updatgps_lockStatus(isLocked);
      setDeviceStatus(getFieldValueById(assetUnitId, "deviceStatusF2"));
      updateBackCoverStatus(detail.backCoverStatus);
      setDeviceLat(detail.latitude);
      setDeviceLong(detail.longitude);
      setNoOfSatellite(detail.numberOfSatellite);
      setGsmSignalQuality(detail.gsmSignalQuality);
      setDateTime(convertUtcToLocal(detail.dateTime));
      setLowBatteryAlert(detail.lowBatteryAlert);
      setBackCoverOpenAlert(detail.backCoverOpenAlert);
      setUnlockingAlert(detail.unlockingAlert);
      setWrongPasswordAlert(detail.wrongPasswordAlert);
      setVibrationAlert(detail.vibrationAlert);
      setLockRopeTamperAlert(detail.lockRopeTamperAlert);
      setUnauthorizedRfidAlert(detail.unauthorizedRfidAlert);
    }
  };

  function updatgps_lockStatus(deviceLocked) {
    setLockStatus(deviceLocked ? Constants.LOCK_CLOSE : Constants.LOCK_OPEN);
  }

  function updateBackCoverStatus(backCoverClosed) {
    setBackCoverStatus(backCoverClosed ? Constants.BACK_COVER_CLOSE : Constants.BACK_COVER_OPEN);
  }

  const handleClearNotification = () => {
    if (popupData) markAlertsAsRead(popupData.map((item) => item.reportId));
    togglePopup(!showPopup);
  };

  const markAlertsAsRead = async (reportIds) => {
    try {
      const endPoint = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/${UrlContants.LOCATION_ALERT_REPORTS}/${UrlContants.MARK_ALERTS_AS_READ}`;
      axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
      const response = await axios.post(endPoint, reportIds);
      if (response.status === 200) {
        showToast("Alerts cleared!", { autoClose: 3000, type: "success", position: "top-center" });
        updateObjectFieldsById(popupData[0].deviceId, { alertStatus: "-" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // --- PREPARE DATA FOR CHILDREN ---

  // Keep the panel's status in sync with the row's status by always reading
  // deviceStatusF2 from apiResponse for the selected device. The local
  // `deviceStatus` state is still updated in click handlers / WS callbacks
  // for the GPS_LOCK / BT_LOCK / MOBI_TRACK flows that override it for their
  // own reasons, so we fall back to it when no row is found.
  const selectedRow = apiResponse.find((d) => d.assetUnitId === selectedDevice);
  // Track Point presence is owned by the shared realtime store (isMobiTrackLive) — the SAME
  // authority the list row reads (DeviceList.getDeviceStatus). Reading it here too guarantees the
  // detail-panel badge and the list row can never disagree, and both flip together on a status
  // toggle (the MOBI_TRACK effect above re-renders this component on every store frame + tick, so
  // this recomputes in lockstep). The old code read the row's deviceStatusF2, which is driven by a
  // *separate* socket subscription and stayed OFFLINE for a driver who was already online before the
  // dashboard opened — so the panel showed OFFLINE while the list showed Online. For every other
  // device type keep deviceStatusF2 (falling back to the local deviceStatus state).
  const liveDeviceStatus =
    selectedRow?.assetType === "MOBI_TRACK"
      ? (isMobiTrackLive(selectedDevice) ? Constants.DEVICE_ONLINE : Constants.DEVICE_OFFLINE)
      : (selectedRow?.deviceStatusF2 ?? deviceStatus);

  const detailsObject = {
    deviceStatus: liveDeviceStatus,
    lockStatus,
    backCoverStatus,
    noOfSatellite,
    gsmSignalQuality,
    dateTime,
    batteryStatus: apiResponse.find((d) => d.assetUnitId === selectedDevice)?.batteryStatusF3,
    deviceLat,
    deviceLong,
    isVisible,
    actionMessage,
    alerts: {
      lowBatteryAlert,
      backCoverOpenAlert,
      vibrationAlert,
      lockRopeTamperAlert,
      unlockingAlert,
      wrongPasswordAlert,
      unauthorizedRfidAlert,
    },
    // GT06-specific
    gt06AccOn,
    gt06Speed,
    gt06VoltageLevel,
    gt06GsmSignal,
  };

  const actionsObject = { showConfirmation };

  /**
   * Bulk instant unlock: sends the unlock command to each selected GPS lock over one STOMP
   * connection. Uses the same legacy `(P43,XXXXXX)` envelope as the single-device unlock — the
   * backend injects each asset's own password. Callers pass only online lock unit ids.
   */
  function bulkInstantUnlock(unitIds) {
    if (!unitIds || unitIds.length === 0) return;
    const socket = new SockJS(Constants.BASE_URL + Constants.GPS_WEBSOCKET);
    const client = Stomp.over(socket);
    client.debug = null;
    client.connect({}, () => {
      unitIds.forEach((deviceId) => {
        try {
          client.send("/app/send-command", {}, JSON.stringify({ command: "(P43,XXXXXX)", deviceId }));
        } catch (e) {
          console.error("[Dashboard] bulk unlock send failed for", deviceId, e);
        }
      });
      setTimeout(() => { if (client?.connected) client.disconnect(); }, 800);
    });
  }

  // For CLIENT_EMPLOYEE: filter apiResponse to only show their allocated assets.
  // allocatedUnitIds is null while loading (show all during that window), then
  // becomes a Set of assetUnitIds once the fetch completes.
  const isEmployee = getRole() === ROLES.CLIENT_EMPLOYEE;
  const effectiveApiResponse = useMemo(() => {
    if (!isEmployee || allocatedUnitIds === null) return apiResponse;
    return apiResponse.filter(d => allocatedUnitIds.has(d.assetUnitId));
  }, [apiResponse, isEmployee, allocatedUnitIds]);

  // Load client labels once for the dashboard label filter (point 2)
  useEffect(() => {
    const loadLabels = async () => {
      try {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-labels`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url);
        if (res.status === 200) setAllLabels(res.data.responseData || []);
      } catch {
        /* labels optional */
      }
    };
    loadLabels();
  }, []);

  // Resolve selected labels → asset ids
  useEffect(() => {
    const resolve = async () => {
      if (selectedLabelIds.length === 0) {
        setLabelAssetIds(null);
        return;
      }
      try {
        const url = `${Constants.BASE_URL}${Constants.API_V1}/${getClientIdOfUser()}/asset-labels/assets`;
        axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
        const res = await axios.get(url, { params: { labelIds: selectedLabelIds.join(","), size: 1000 } });
        const ids = (res.data.responseData || []).map((d) => d.asset?.assetId).filter((v) => v != null);
        setLabelAssetIds(new Set(ids));
      } catch {
        setLabelAssetIds(new Set());
      }
    };
    resolve();
  }, [selectedLabelIds]);

  const toggleLabelFilter = (labelId) =>
    setSelectedLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );

  const deviceTabCounts = useMemo(() => ({
    GPS_LOCK: effectiveApiResponse.filter((d) => d.assetType === "GPS_LOCK").length,
    BT_LOCK: effectiveApiResponse.filter((d) => d.assetType === "BT_LOCK").length,
    MOBI_TRACK: effectiveApiResponse.filter((d) => d.assetType === "MOBI_TRACK").length,
    GT06_TRACKER: effectiveApiResponse.filter((d) => d.assetType === "GT06_TRACKER").length,
  }), [effectiveApiResponse]);

  const filteredDevices = useMemo(() => {
    let result = effectiveApiResponse.filter((d) => d.assetType === activeDeviceTab);

    // MOBI_TRACK ONLINE/OFFLINE is resolved asynchronously in DeviceList — skip here
    const skipFilter =
      activeDeviceTab === "MOBI_TRACK" && (quickFilter === "ONLINE" || quickFilter === "OFFLINE");

    if (quickFilter !== "DEFAULT" && !skipFilter) {
      result = result.filter((d) => {
        switch (quickFilter) {
          case "ONLINE":
            return d.deviceStatusF2 === Constants.DEVICE_ONLINE;
          case "OFFLINE":
            return d.deviceStatusF2 !== Constants.DEVICE_ONLINE;
          case "MOTION":
            return typeof d.assetMovementStatusF5 === "string" && d.assetMovementStatusF5.toLowerCase().includes("moving");
          case "IDLE":
            return d.assetMovementStatusF5 === "Idle" || d.assetMovementStatusF5 === Constants.NO_INFO || d.assetMovementStatusF5 === "N/A" || d.assetMovementStatusF5 === "NA";
          case "LOCKED":
            return d.lockStatusF4 === Constants.LOCKED || d.lockStatusF4 === "LOCKED";
          case "UNLOCKED":
            return d.lockStatusF4 === Constants.OPEN || d.lockStatusF4 === "Open";
          case "ENGINE_ON":
            // Only count online devices with ignition on — offline devices keep stale accOn from last report
            return d.accOn === true && d.deviceStatusF2 === Constants.DEVICE_ONLINE;
          case "ENGINE_OFF":
            return !(d.accOn === true && d.deviceStatusF2 === Constants.DEVICE_ONLINE);
          default:
            return true;
        }
      });
    }

    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (d) =>
          (d.name && d.name.toLowerCase().includes(query)) ||
          (d.assetUnitId && d.assetUnitId.toLowerCase().includes(query))
      );
    }

    // Label filter (point 2) — when active, keep only devices whose assetId is in the resolved set
    if (labelAssetIds) {
      result = result.filter((d) => labelAssetIds.has(d.assetId));
    }

    return result;
  }, [effectiveApiResponse, quickFilter, activeDeviceTab, searchQuery, labelAssetIds]);

  const resetAllFilters = () => { setQuickFilter("DEFAULT"); setSearchQuery(""); };

  return (
    <>
      {isLoading ? (
        <LoadingBanner />
      ) : (
        <div className="dashboard-shell">
          {/* Compact, role-aware page header — added on top of the
              existing 2-column workspace so the dashboard now matches
              the rest of the redesigned screens (Users/Assets/Reports).
              The header is purely chrome; it does not touch the
              device-list, details-rail, or insights data flow. */}
          <DashboardHeader
            isEmployee={isEmployee}
            deviceCount={effectiveApiResponse?.length || 0}
            onRefresh={fetchAndUpdateLocation}
            isRefreshing={isRefreshingLocation}
          />
          {/* Three zones: ① device list (left) · ② details (middle) · ③ analytics (right) */}
          <div className="dashboard-workspace dash-2col">
            <div className="dash-left">
              <DeviceList
                devices={filteredDevices}
                selectedDeviceId={selectedDevice}
                locationMap={locationMap}
                isRefreshingLocation={isRefreshingLocation}
                onRefreshLocation={fetchAndUpdateLocation}
                onRowClick={handleRowClick}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                activeDeviceTab={activeDeviceTab}
                setActiveDeviceTab={setActiveDeviceTab}
                deviceTabCounts={deviceTabCounts}
                onResetFilters={resetAllFilters}
                viewMode={viewMode}
                setViewMode={setViewMode}
                quickFilter={quickFilter}
                setQuickFilter={setQuickFilter}
                deviceTypeActions={<DeviceTypeActions activeDeviceTab={activeDeviceTab} />}
                passcodeFeature={isPasscodeFeatureEnabled}
                onInstantUnlock={bulkInstantUnlock}
                labelFilter={
                  allLabels.length > 0 ? (
                    <LabelMultiSelect
                      labels={allLabels}
                      selectedIds={selectedLabelIds}
                      onToggle={toggleLabelFilter}
                      placeholder="Filter by label…"
                    />
                  ) : null
                }
              />
            </div>

            {/* RIGHT column: selected-device details (top) → analytics (below) */}
            <div className="dash-right-rail" ref={detailsPanelRef}>
              {selectedDevice && (
                <DeviceDetailsPanel
                  selectedDevice={selectedDevice}
                  assetName={assetName}
                  assetId={selectedAssetId}
                  assetType={selectedAssetTypeForPanel}
                  vehicleType={selectedRow?.vehicleType ?? null}
                  protocol={selectedRow?.protocol ?? null}
                  details={detailsObject}
                  actions={actionsObject}
                  onClose={closeDetailsPanel}
                  passcodeFeature={isPasscodeFeatureEnabled}
                />
              )}
              <DashboardInsights
                compact
                devices={effectiveApiResponse}
                scopeLabel={isEmployee ? "Your assigned assets" : "Fleet overview"}
              />
            </div>
          </div>

          <AlertPopupDialog
            showPopup={showPopup}
            togglePopup={togglePopup}
            popupData={popupData}
            clearNotifications={handleClearNotification}
          />
        </div>
      )}
    </>
  );
}
